import { css } from "styled-components";
import { toFixed } from "@shared/utils/numbers";
import { USD_PRECISION } from "features/affiliation/common/constants";

/** Name of the CSS container the affiliation layouts respond to. */
export const AFFILIATION_CONTAINER = "affiliation";

/**
 * USD with two decimals and thousands separators, e.g. "$1,351.30"; "—"
 * without a value. Cut (not rounded) to the cents like the classic look, so
 * a balance of 99.999 reads $99.99, what can actually be paid out.
 */
export const formatUsd = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(Number(value)))
    return "—";
  const number = toFixed(Number(value), USD_PRECISION);
  const formatted = Math.abs(number).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  });
  return `${number < 0 ? "−" : ""}$${formatted}`;
};

/** Drops a trailing colon from a label ("Balance:" -> "Balance"). */
export const stripColon = (label: string) => label.replace(/\s*:\s*$/, "");

/** Parses a figure like "$7.8" or 12 into a number. */
export const toNumber = (value?: number | string) =>
  typeof value === "number"
    ? value
    : Number(String(value ?? "").replace(/[^0-9.-]/g, "")) || 0;

/** A share as a percentage: one decimal under 10% ("0.9%"), whole above. */
export const formatShare = (share: number) => {
  const percent = share * 100;
  return `${percent < 10 ? percent.toFixed(1) : Math.round(percent)}%`;
};

/** New-look table columns of numbers: right-aligned (TablePanel styles). */
export const NUMBER_COLUMN = {
  align: "right" as const,
  className: "nl-number",
};

/* ---- Kept for features/payouts (invoices, payout methods) ---- */

/** Numbers line up in columns. */
export const tabularNums = css`
  font-variant-numeric: tabular-nums;
`;

/**
 * The new-look table treatment: untinted header on a hairline, no shaded
 * sorted column, calm row hover and numbers in columns.
 */
export const newLookTableStyles = css`
  .ant-table-wrapper .ant-table {
    background: transparent;
  }
  .ant-table-content {
    overflow-x: auto;
  }
  .ant-table-wrapper .ant-table-thead > tr > th,
  .ant-table-wrapper .ant-table-thead > tr > td {
    background: transparent !important;
    border-bottom: ${(p) => p.theme.borders.lightest};
    padding: 10px 10px;
    font-size: 12.5px;
    white-space: nowrap;
  }
  .ant-table-wrapper .ant-table-thead th.ant-table-column-has-sorters:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor} !important;
  }
  /* One size and line box for every cell (plain values and the Text
     components alike), so the texts of a row share a baseline. */
  .ant-table-wrapper .ant-table-tbody > tr > td {
    padding: 12px 10px;
    border-bottom: ${(p) => p.theme.borders.lightest};
    font-size: 14px;
    line-height: 22px;
    white-space: nowrap;
    ${tabularNums};
  }
  .ant-table-wrapper .ant-table-tbody > tr > td.ant-table-column-sort {
    background: transparent !important;
  }
  .ant-table-wrapper .ant-table-tbody > tr:last-child > td {
    border-bottom: none;
  }
  .ant-table-wrapper .ant-table-tbody > tr.ant-table-row:hover > td,
  .ant-table-wrapper
    .ant-table-tbody
    .ant-table-row
    > .ant-table-cell-row-hover {
    background: ${(p) => p.theme.colors.cardLightBgColor} !important;
  }
  .ant-table-wrapper .ant-table-thead > tr > th:first-child,
  .ant-table-wrapper .ant-table-tbody > tr > td:first-child {
    padding-left: 4px;
  }
  .ant-table-column-sorters {
    justify-content: flex-start;
    gap: 4px;
    .ant-table-column-title {
      flex: none;
    }
  }
  .ant-table-pagination.ant-pagination {
    margin: 12px 0 4px;
  }
`;

/** A tappable/hoverable square icon button (row actions, kebabs). */
export const iconButtonStyles = css`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  color: ${(p) => p.theme.colors.secondaryText};
  cursor: pointer;
  transition: background 0.15s, color 0.15s;
  &:hover {
    background: ${(p) => p.theme.colors.cardLightBgColor};
    color: ${(p) => p.theme.colors.titleColor};
  }
`;
